import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Filter, ChevronDown, ChevronUp, RotateCcw, Search } from 'lucide-react';

export const PERIOD_OPTIONS = [
  { value: 'this_month', label: 'Bulan Ini' },
  { value: 'last_month', label: 'Bulan Lalu' },
  { value: 'this_quarter', label: 'Triwulan Ini' },
  { value: 'this_year', label: 'Tahun Ini' },
  { value: 'month', label: 'Bulan Tertentu' },
  { value: 'quarter', label: 'Triwulan Tertentu' },
  { value: 'custom', label: 'Custom Range' },
];

const MONTH_NAMES = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];

const QUARTERS = [
  { value: 1, label: 'Q1 (Jan – Mar)' },
  { value: 2, label: 'Q2 (Apr – Jun)' },
  { value: 3, label: 'Q3 (Jul – Sep)' },
  { value: 4, label: 'Q4 (Okt – Des)' },
];

export const DATE_BASIS_OPTIONS = [
  { value: 'invoice_date', label: 'Tanggal Invoice' },
  { value: 'event_date', label: 'Tanggal Event' },
  { value: 'payment_date', label: 'Tanggal Pembayaran' },
  { value: 'due_date', label: 'Tanggal Jatuh Tempo' },
];

export const TAX_FILTER_OPTIONS = [
  { value: 'all', label: 'Semua Pajak' },
  { value: 'with_ppn', label: 'Dengan PPN' },
  { value: 'without_ppn', label: 'Tanpa PPN' },
  { value: 'with_pph', label: 'Dengan PPh' },
  { value: 'without_pph', label: 'Tanpa PPh' },
  { value: 'waiting_bukti', label: 'Menunggu Bukti Potong' },
  { value: 'received_bukti', label: 'Bukti Potong Received' },
];

export const PROOF_FILTER_OPTIONS = [
  { value: 'all', label: 'Semua' },
  { value: 'with_proof', label: 'Ada Bukti Transfer' },
  { value: 'without_proof', label: 'Belum Ada Bukti Transfer' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: '', label: 'Semua Metode' },
  { value: 'Bank Transfer', label: 'Transfer Bank' },
  { value: 'Cash', label: 'Tunai' },
  { value: 'QRIS', label: 'QRIS' },
  { value: 'E-Wallet', label: 'E-Wallet' },
  { value: 'Other', label: 'Lainnya' },
];

export function defaultFilters() {
  const today = new Date();
  return {
    period: 'this_month',
    month: today.getMonth() + 1,
    quarter: Math.floor(today.getMonth() / 3) + 1,
    year: today.getFullYear(),
    date_from: '',
    date_to: '',
    date_basis: 'invoice_date',
    client_id: '',
    status: '',
    venue: '',
    payment_method: '',
    tax_filter: 'all',
    proof_filter: 'all',
  };
}

export function paramsFromFilters(f) {
  const out = { period: f.period, date_basis: f.date_basis };
  if (f.period === 'month') out.month = f.month;
  if (f.period === 'quarter') out.quarter = f.quarter;
  if (['month', 'quarter', 'this_year'].includes(f.period)) out.year = f.year;
  if (f.period === 'custom') {
    if (f.date_from) out.date_from = f.date_from;
    if (f.date_to) out.date_to = f.date_to;
  }
  for (const k of ['client_id', 'status', 'venue', 'payment_method', 'tax_filter', 'proof_filter']) {
    if (f[k] && f[k] !== 'all') out[k] = f[k];
  }
  return out;
}

export function ReportFilterBar({ filters, setFilters, onApply, onReset, clients = [], extra = null }) {
  const [open, setOpen] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const yearList = (() => {
    const cy = new Date().getFullYear();
    return [cy + 1, cy, cy - 1, cy - 2, cy - 3, cy - 4];
  })();
  const showMonth = filters.period === 'month';
  const showQuarter = filters.period === 'quarter';
  const showCustom = filters.period === 'custom';
  const showYear = ['month', 'quarter', 'this_year'].includes(filters.period);

  return (
    <Card className="bg-card border-border" data-testid="report-filter-card">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-heading flex items-center gap-2 text-slate-100">
          <Filter className="h-4 w-4 text-primary" /> Filter Laporan
        </CardTitle>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1"
          data-testid="report-filter-toggle"
        >
          {open ? <>Sembunyikan <ChevronUp className="h-3 w-3" /></> : <>Tampilkan <ChevronDown className="h-3 w-3" /></>}
        </button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          {/* Row 1 — Periode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] text-slate-300">Periode</Label>
              <Select value={filters.period} onValueChange={(v) => setFilters(p => ({ ...p, period: v }))}>
                <SelectTrigger className="h-10 text-slate-100" data-testid="report-filter-period"><SelectValue /></SelectTrigger>
                <SelectContent>{PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {showMonth && (
              <div className="space-y-1.5">
                <Label className="text-[11px] text-slate-300">Bulan</Label>
                <Select value={String(filters.month)} onValueChange={(v) => setFilters(p => ({ ...p, month: Number(v) }))}>
                  <SelectTrigger className="h-10 text-slate-100" data-testid="report-filter-month"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTH_NAMES.map((n, i) => <SelectItem key={i + 1} value={String(i + 1)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {showQuarter && (
              <div className="space-y-1.5">
                <Label className="text-[11px] text-slate-300">Triwulan</Label>
                <Select value={String(filters.quarter)} onValueChange={(v) => setFilters(p => ({ ...p, quarter: Number(v) }))}>
                  <SelectTrigger className="h-10 text-slate-100" data-testid="report-filter-quarter"><SelectValue /></SelectTrigger>
                  <SelectContent>{QUARTERS.map(q => <SelectItem key={q.value} value={String(q.value)}>{q.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {showYear && (
              <div className="space-y-1.5">
                <Label className="text-[11px] text-slate-300">Tahun</Label>
                <Select value={String(filters.year)} onValueChange={(v) => setFilters(p => ({ ...p, year: Number(v) }))}>
                  <SelectTrigger className="h-10 text-slate-100" data-testid="report-filter-year"><SelectValue /></SelectTrigger>
                  <SelectContent>{yearList.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {showCustom && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-300">Dari Tanggal</Label>
                  <Input type="date" value={filters.date_from} onChange={(e) => setFilters(p => ({ ...p, date_from: e.target.value }))} className="h-10 text-slate-100" data-testid="report-filter-from" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-300">Sampai Tanggal</Label>
                  <Input type="date" value={filters.date_to} onChange={(e) => setFilters(p => ({ ...p, date_to: e.target.value }))} className="h-10 text-slate-100" data-testid="report-filter-to" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label className="text-[11px] text-slate-300">Jenis Laporan Berdasarkan</Label>
              <Select value={filters.date_basis} onValueChange={(v) => setFilters(p => ({ ...p, date_basis: v }))}>
                <SelectTrigger className="h-10 text-slate-100" data-testid="report-filter-date-basis"><SelectValue /></SelectTrigger>
                <SelectContent>{DATE_BASIS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400">Pilih tanggal yang menjadi dasar filter laporan.</p>
            </div>
          </div>

          {/* Advanced filter toggle */}
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setAdvancedOpen(o => !o)}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-300 hover:text-white"
              data-testid="report-filter-advanced-toggle"
            >
              <Search className="h-3.5 w-3.5" />
              Filter Lanjutan
              {advancedOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {advancedOpen && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 rounded-lg border border-border bg-card/40 p-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-300">Client</Label>
                  <Select value={filters.client_id || 'all'} onValueChange={(v) => setFilters(p => ({ ...p, client_id: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="h-10 text-slate-100" data-testid="report-filter-client"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Client</SelectItem>
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-300">Venue</Label>
                  <Input value={filters.venue} onChange={(e) => setFilters(p => ({ ...p, venue: e.target.value }))} placeholder="Cari venue…" className="h-10 text-slate-100" data-testid="report-filter-venue" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-300">Metode Pembayaran</Label>
                  <Select value={filters.payment_method || 'all'} onValueChange={(v) => setFilters(p => ({ ...p, payment_method: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="h-10 text-slate-100" data-testid="report-filter-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Metode</SelectItem>
                      {PAYMENT_METHOD_OPTIONS.filter(o => o.value).map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-300">Status Pajak</Label>
                  <Select value={filters.tax_filter} onValueChange={(v) => setFilters(p => ({ ...p, tax_filter: v }))}>
                    <SelectTrigger className="h-10 text-slate-100" data-testid="report-filter-tax"><SelectValue /></SelectTrigger>
                    <SelectContent>{TAX_FILTER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-300">Bukti Transfer</Label>
                  <Select value={filters.proof_filter} onValueChange={(v) => setFilters(p => ({ ...p, proof_filter: v }))}>
                    <SelectTrigger className="h-10 text-slate-100" data-testid="report-filter-proof"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROOF_FILTER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {extra}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onReset} data-testid="report-filter-reset" className="text-slate-200 hover:text-white">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset Filter
            </Button>
            <Button size="sm" onClick={onApply} data-testid="report-filter-apply">Terapkan Filter</Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
